import React, { useEffect, useRef, useState } from 'react';
import { useMantineColorScheme } from '@mantine/core';
import * as d3 from 'd3';
import './cashFlowChartStyle.scss';
import { useTranslation } from 'react-i18next';
import { formatEnUsValue, useCurrency } from "../../utils/helpers";

const getTrailingDigits = (value) => {
  const text = String(value ?? '');
  let end = text.length;

  while (end > 0) {
    const code = text.charCodeAt(end - 1);
    if (code < 48 || code > 57) break;
    end -= 1;
  }

  return end === text.length ? null : text.slice(end);
};

const SERIES_META = [
  { key: 'totalCapex', color: '#4992ff' },
  { key: 'totalOpex', color: '#7cffb2' },
  { key: 'totalIncentive', color: '#fddd60' },
  { key: 'totalFuel', color: '#ff6e76' },
  { key: 'totalReplacement', color: '#58d9f9' },
  { key: 'totalSalvage', color: '#05c091' }
];

const CashFlowChart = ({ data = [], isDiscounted = false }) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const currency = useCurrency();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const legendRef = useRef(null);
  const panStateRef = useRef({ active: false, startX: 0, startRange: [0, 1] });
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const [zoomRange, setZoomRange] = useState([0, 1]);
  const [isPanEnabled, setIsPanEnabled] = useState(false);
  const resizeObserverRef = useRef(null);
  const fontFamily = 'Nunito, sans-serif';

  // Normalize incoming data (nominal vs discounted)
  const normalized = (data || []).map(item => {
    const src = isDiscounted ? item?.discountedValue : item?.nominalValue;
    return {
      totalCapex: src?.totalCapex ?? 0,
      totalOpex: src?.totalOpex ?? 0,
      totalIncentive: src?.totalIncentive ?? 0,
      totalFuel: src?.totalFuel ?? 0,
      totalReplacement: src?.totalReplacement ?? 0,
      totalSalvage: src?.totalSalvage ?? 0,
    };
  });
  const years = normalized.map((_, i) => `${t('result.cashFlow.year')} ${i+1}`);
  const seriesWithLabels = SERIES_META.map(s => ({
    ...s,
    label: t(`result.cashFlow.series.${s.key}`)
  }));

  function draw(){
    const root = containerRef.current; if(!root) return;
    const legendHost = legendRef.current;
    const existing = svgRef.current; if (existing) existing.remove();
    const width = root.clientWidth;

    // Compute visible years from zoom range first so layout can adapt to label density
    const totalYears = years.length || 1;
    let [zs, ze] = zoomRange; zs = Math.max(0, Math.min(1, zs)); ze = Math.max(zs, Math.min(1, ze));
    let startIdx = Math.floor(zs * totalYears);
    let endIdx = Math.ceil(ze * totalYears);
    if (endIdx <= startIdx) endIdx = Math.min(totalYears, startIdx + 1);
    const visibleYears = years.slice(startIdx, endIdx);

    const shouldRotateTicks = visibleYears.length > 12;
    // Dynamically calculate chart height based on container width (responsive, no scroll)
    const minHeight = 220;
    const maxHeight = 350;
    // Use container aspect ratio for height, clamp to min/max
    const height = Math.max(minHeight, Math.min(maxHeight, width * 0.35));
    const margin = { top: 32, right: 24, bottom: shouldRotateTicks ? 72 : 48, left: 60 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const activeSeries = seriesWithLabels.filter(s => !hiddenKeys.has(s.key));

    // Compute y-domain only from visible (zoomed) years so axis rescales appropriately
    const visibleNormalized = normalized.slice(startIdx, endIdx);
    const stackedRangeVals = [];
    visibleNormalized.forEach(row => {
      activeSeries.forEach(s => stackedRangeVals.push(row[s.key]));
    });
    const minVal = d3.min(stackedRangeVals.concat([0])) ?? 0;
    const maxVal = d3.max(stackedRangeVals.concat([0])) ?? 0;

    const svg = d3.select(root).append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('background', colorScheme==='dark'? '#111111':'#ffffff')
      .style('font-family', fontFamily);
    svgRef.current = svg.node();

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Title
    svg.append('text')
      .attr('x', margin.left)
      .attr('y', 28)
      .attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057')
      .attr('font-size', 16)
      .attr('font-weight', 600)
      .text(t('result.cashFlow.title'));

    // Legend (HTML for easier toggling) - show all series in fixed order with color swatches
    if (legendHost) {
      legendHost.innerHTML = '';
      seriesWithLabels.forEach(s => {
        const isHidden = hiddenKeys.has(s.key);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-pressed', (!isHidden).toString());
        btn.setAttribute('data-series', s.key);
        btn.style.display = 'flex';
        btn.style.alignItems = 'center';
        btn.style.gap = '6px';
        btn.style.margin = '0 10px 6px 0';
        btn.style.padding = '4px 10px';
        btn.style.fontSize = '12px';
        btn.style.fontFamily = fontFamily;
        btn.style.borderRadius = '4px';
        btn.style.border = '1px solid ' + (colorScheme === 'dark' ? '#444' : '#ccc');
        btn.style.background = colorScheme === 'dark' ? (isHidden ? '#1c1c1c' : '#222832') : (isHidden ? '#f5f5f5' : '#ffffff');
        btn.style.cursor = 'pointer';
        btn.style.transition = 'background .15s, opacity .15s';
        btn.style.opacity = isHidden ? 0.55 : 1;
        btn.onmouseenter = () => { if(!isHidden) btn.style.background = colorScheme==='dark'? '#2a303b':'#f0f6fa'; };
        btn.onmouseleave = () => { btn.style.background = colorScheme === 'dark' ? (isHidden ? '#1c1c1c' : '#222832') : (isHidden ? '#f5f5f5' : '#ffffff'); };

        const sw = document.createElement('span');
        sw.style.display = 'inline-block';
        sw.style.width = '14px';
        sw.style.height = '14px';
        sw.style.borderRadius = '3px';
        sw.style.boxShadow = '0 0 0 1px ' + (colorScheme==='dark'? '#000':'#fff') + ' inset, 0 0 0 1px rgba(0,0,0,0.15)';
        sw.style.background = s.color;
        sw.title = s.label + ' ' + t('common.color');

        const label = document.createElement('span');
        label.textContent = s.label;
        label.style.whiteSpace = 'nowrap';
        if (isHidden) {
          label.style.textDecoration = 'line-through';
        }

        btn.appendChild(sw);
        btn.appendChild(label);
        btn.onclick = () => {
          const next = new Set(hiddenKeys);
          if (next.has(s.key)) {
            next.delete(s.key);
          } else {
            next.add(s.key);
          }
          setHiddenKeys(next);
        };
        legendHost.appendChild(btn);
      });
    }

    const x0 = d3.scaleBand().domain(visibleYears).range([0, innerW]).padding(0.25);
    const x1 = d3.scaleBand().domain(activeSeries.map(s=> s.key)).range([0, x0.bandwidth()]).padding(0.15);
    const y = d3.scaleLinear().domain([minVal, maxVal * 1.15]).nice().range([innerH, 0]);

    const minTickSpacing = shouldRotateTicks ? 36 : 64;
    const maxVisibleTicks = Math.max(2, Math.floor(innerW / minTickSpacing));
    const tickStep = visibleYears.length > maxVisibleTicks ? Math.ceil(visibleYears.length / maxVisibleTicks) : 1;
    const xTickValues = visibleYears.filter((year, index) => index % tickStep === 0 || index === visibleYears.length - 1);

    const xAxis = d3.axisBottom(x0)
      .tickValues(xTickValues)
      .tickFormat((value) => {
        if (visibleYears.length > 25 || tickStep > 1) {
          const trailingDigits = getTrailingDigits(value);
          return trailingDigits ?? value;
        }
        return value;
      });
    
    // Custom formatter for y-axis: K (thousand), M (million), B (billion), T (trillion), etc.
    const formatYAxis = (d) => {
      const absValue = Math.abs(d);
      const suffixes = ['', 'K', 'M', 'B', 'T', 'Q']; // K, M, B, T (trillion), Q (quadrillion)
      let suffixIndex = 0;
      let scaledValue = absValue;
      
      while (scaledValue >= 1000 && suffixIndex < suffixes.length - 1) {
        scaledValue /= 1000;
        suffixIndex++;
      }
      
      // Return formatted value with appropriate suffix
      return (d < 0 ? '-' : '') + scaledValue.toFixed(0) + suffixes[suffixIndex];
    };
    
    const yAxis = d3.axisLeft(y).ticks(5).tickFormat(formatYAxis);

    g.append('g').attr('transform', `translate(0,${innerH})`).call(xAxis)
      .call(sel=> sel.selectAll('text')
        .attr('font-size',11)
        .attr('font-weight',500)
        .attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057')
        .attr('text-anchor', shouldRotateTicks ? 'end' : 'middle')
        // .attr('transform', shouldRotateTicks ? 'rotate(-35)' : null)
        .attr('dx', shouldRotateTicks ? '-0.45em' : null)
        .attr('dy', shouldRotateTicks ? '0.2em' : '0.71em'))
      .call(sel=> sel.selectAll('line,path').attr('stroke','none'));
    g.append('g').call(yAxis)
      .call(sel=> sel.selectAll('text').attr('font-size',11).attr('font-weight',500).attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057'))
      .call(sel=> sel.selectAll('line,path').attr('stroke','none'));

    // Axis labels
    g.append('text').attr('x', innerW/2).attr('y', innerH + 34).attr('text-anchor','middle').attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057').attr('font-size',12).text(t('result.cashFlow.axis.xYears'));
    g.append('text').attr('transform','rotate(-90)').attr('x', -innerH/2).attr('y', -46).attr('text-anchor','middle').attr('fill', colorScheme==='dark'? '#d5d7e0':'#495057').attr('font-size',12).text(`${t('result.cashFlow.axis.yCost')} (${currency})`);

    // Tooltip creation
    if (!tooltipRef.current){
      const t = document.createElement('div');
      t.style.position='absolute'; t.style.pointerEvents='none'; t.style.background=colorScheme==='dark'? '#222':'#fff';
      t.style.border='1px solid '+(colorScheme==='dark'? '#555':'#666'); t.style.padding='6px 8px'; t.style.fontSize='12px'; t.style.fontFamily=fontFamily;
      t.style.borderRadius='4px'; t.style.boxShadow='0 2px 6px rgba(0,0,0,.25)'; t.style.display='none'; t.style.zIndex=20;
      tooltipRef.current = t; root.appendChild(t); root.style.position='relative';
    }
    const tooltip = tooltipRef.current; tooltip.innerHTML='';

    // Groups per year
    const yearGroups = g.selectAll('.year-group').data(visibleYears).enter().append('g')
      .attr('class','year-group')
      .attr('transform', d => {
        const x = x0(d);
        // Defensive: if x is NaN, fallback to 0
        return `translate(${isNaN(x) ? 0 : x},0)`;
      });

    // Bars per active series
    yearGroups.selectAll('rect').data(d => activeSeries.map(s=> ({
      key:s.key, label:s.label, color:s.color, year:d, value: (normalized[years.indexOf(d)]||{})[s.key] || 0 })) )
      .enter().append('rect')
      .attr('x', d=> x1(d.key))
      .attr('width', x1.bandwidth())
      .attr('y', d=> y(Math.max(0,d.value)))
      .attr('height', d=> Math.abs(y(d.value) - y(0)))
      .attr('fill', d=> d.color)
      .attr('opacity',1)
      .attr('data-orig-fill', d => d.color)
      .style('cursor', 'pointer')
      .on('mouseenter', function(evt, d){
        const rect = d3.select(this);
        const orig = rect.attr('data-orig-fill') || d.color;
        let highlight;
        try{
          highlight = d3.color(orig).darker(0.8).formatHex();
        }catch(e){
          highlight = '#228be6';
        }
        rect.attr('fill', highlight);
      })
      .on('mousemove', function(evt,d){
        const idx = years.indexOf(d.year);
        const row = normalized[idx];
         const html = `<strong>${d.year}</strong><br/>` + activeSeries.map(s=> `<span style="color:${s.color}">●</span> ${s.label}: <b>${currency} ${formatEnUsValue(row[s.key] ?? 0)}</b>`).join('<br/>');
         tooltip.innerHTML = html;
         const bounds = root.getBoundingClientRect();
        // Place tooltip near cursor without covering the hovered bar.
        // Prefer to the right and below the cursor; if near edges, place to left/above.
        const offsetX = 12;
        const offsetY = 8;
        // Make it visible but hidden to measure size
        tooltip.style.display = 'block';
        tooltip.style.visibility = 'hidden';
        const ttRect = tooltip.getBoundingClientRect();
        // Default: to the right and slightly below cursor (viewport coords)
        let left = evt.clientX + offsetX;
        let top = evt.clientY + offsetY;
        // If overflowing right edge of container, show to the left of cursor
        if (left + ttRect.width > bounds.right) {
          left = evt.clientX - offsetX - ttRect.width;
        }
        // If overflowing bottom edge, show above cursor
        if (top + ttRect.height > bounds.bottom) {
          top = evt.clientY - offsetY - ttRect.height;
        }
        // Clamp inside container bounds
        const minLeft = bounds.left + 4;
        const maxLeft = bounds.right - ttRect.width - 4;
        const minTop = bounds.top + 4;
        const maxTop = bounds.bottom - ttRect.height - 4;
        left = Math.max(minLeft, Math.min(left, maxLeft));
        top = Math.max(minTop, Math.min(top, maxTop));
        // Convert back to container-relative coordinates
        tooltip.style.left = (left - bounds.left) + 'px';
        tooltip.style.top = (top - bounds.top) + 'px';
        tooltip.style.visibility = 'visible';
       })
      .on('mouseleave', function(evt,d){
        const rect = d3.select(this);
        const orig = rect.attr('data-orig-fill') || d.color;
        rect.attr('fill', orig);
        tooltip.style.display='none';
      });

    // If negative values exist draw a zero line
    if (minVal < 0 && maxVal > 0){
      const yZero = y(0);
      g.append('line')
        .attr('x1',0).attr('x2',innerW)
        .attr('y1', isNaN(yZero) ? 0 : yZero)
        .attr('y2', isNaN(yZero) ? 0 : yZero)
        .attr('stroke', colorScheme==='dark'? '#888':'#999')
        .attr('stroke-width',1);
    }

    const currentSpan = Math.max(0, Math.min(1, ze - zs));
    const canPan = currentSpan < 0.999;
    svg.style('cursor', canPan && isPanEnabled ? 'grab' : 'default');

    svg.on('dblclick', (event) => {
      if (!canPan) return;
      event.preventDefault();
      setIsPanEnabled(prev => !prev);
    });

    if (canPan && isPanEnabled) {
      svg.on('mousedown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        panStateRef.current = {
          active: true,
          startX: event.clientX,
          startRange: [zs, ze],
        };
        svg.style('cursor', 'grabbing');
      });

      d3.select(window)
        .on('mousemove.cashflow-pan', (event) => {
          if (!panStateRef.current.active) return;
          const [startZoom, endZoom] = panStateRef.current.startRange;
          const span = endZoom - startZoom;
          const maxStart = Math.max(0, 1 - span);
          if (maxStart <= 0 || innerW <= 0) return;

          const deltaX = event.clientX - panStateRef.current.startX;
          const deltaNorm = deltaX / innerW * maxStart;
          const nextStart = Math.max(0, Math.min(maxStart, startZoom - deltaNorm));
          setZoomRange([nextStart, nextStart + span]);
        })
        .on('mouseup.cashflow-pan', () => {
          if (!panStateRef.current.active) return;
          panStateRef.current.active = false;
          svg.style('cursor', 'grab');
        });
    } else {
      svg.on('mousedown', null);
      d3.select(window).on('mousemove.cashflow-pan', null).on('mouseup.cashflow-pan', null);
    }
  }

  // Zoom helpers
  const zoomIn = () => {
    const [s, e] = zoomRange;
    const total = years.length || 1;
    const rangeSpan = e - s;
    if (rangeSpan <= 1 / total) return;
    const newSpan = Math.max(rangeSpan / 2, 1 / total);
    const center = s + rangeSpan / 2;
    let ns = center - newSpan / 2; let ne = center + newSpan / 2;
    if (ns < 0) { ne -= ns; ns = 0; }
    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
    setZoomRange([Math.max(0, ns), Math.min(1, ne)]);
  };

  const zoomOut = () => {
    const [s, e] = zoomRange;
    const total = years.length || 1;
    const rangeSpan = e - s;
    const newSpan = Math.min(rangeSpan * 2, 1);
    const center = s + rangeSpan / 2;
    let ns = center - newSpan / 2; let ne = center + newSpan / 2;
    if (ns < 0) { ne -= ns; ns = 0; }
    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
    setZoomRange([Math.max(0, ns), Math.min(1, ne)]);
  };

  const zoomReset = () => {
    setZoomRange([0, 1]);
  };

  useEffect(() => { draw(); }, [colorScheme, data, hiddenKeys, isDiscounted, zoomRange, isPanEnabled]);

  useEffect(() => {
    const span = Math.max(0, Math.min(1, zoomRange[1] - zoomRange[0]));
    if (span >= 0.999 && isPanEnabled) {
      setIsPanEnabled(false);
    }
  }, [zoomRange, isPanEnabled]);

  useEffect(() => {
    if (!resizeObserverRef.current){
      resizeObserverRef.current = new ResizeObserver(()=> draw());
      if (containerRef.current) resizeObserverRef.current.observe(containerRef.current);
    }
    return () => {
      if (resizeObserverRef.current && containerRef.current){
        resizeObserverRef.current.unobserve(containerRef.current);
      }
      d3.select(window).on('mousemove.cashflow-pan', null).on('mouseup.cashflow-pan', null);
    };
  }, []);

  return (
    <div className='chart-container' ref={containerRef} style={{ width:'100%', height: 'auto', maxWidth: '100%', fontFamily: fontFamily, zoom:"118%" }}>
      <div style={{ display:'flex', justifyContent:'flex-end', margin:'4px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            aria-label="cashflow-zoom-in"
            title="Zoom In"
            onClick={zoomIn}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057' }}
          >
            +
          </button>
          <button
            type="button"
            aria-label="cashflow-zoom-out"
            title="Zoom Out"
            onClick={zoomOut}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057' }}
          >
            -
          </button>
          <button
            type="button"
            aria-label="cashflow-reset-zoom"
            title="Reset Zoom"
            onClick={zoomReset}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057' }}
          >
            Reset
          </button>
          <select
          title={t('common.download')}
          style={{
            padding:'6px 10px', fontSize:'12px', border:'1px solid #ccc', borderRadius:'4px', cursor:'pointer',
            background: colorScheme==='dark'? '#222832':'#ffffff', color: colorScheme==='dark'? '#d5d7e0':'#495057'
          }}
          value=""
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'png') {
              // Download PNG
              const svgEl = svgRef.current;
              if (!svgEl) return;
              const serializer = new XMLSerializer();
              const cloned = svgEl.cloneNode(true);
              const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
              bg.setAttribute('x','0');
              bg.setAttribute('y','0');
              bg.setAttribute('width', svgEl.clientWidth || svgEl.getAttribute('width') || 1200);
              bg.setAttribute('height', svgEl.clientHeight || svgEl.getAttribute('height') || 600);
              bg.setAttribute('fill', colorScheme==='dark'? '#111111':'#ffffff');
              cloned.insertBefore(bg, cloned.firstChild);
              const svgString = serializer.serializeToString(cloned);
              const img = new Image();
              const canvas = document.createElement('canvas');
              const w = svgEl.clientWidth || parseInt(svgEl.getAttribute('width'),10) || 1200;
              const h = svgEl.clientHeight || parseInt(svgEl.getAttribute('height'),10) || 600;
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext('2d');
              img.onload = () => {
                ctx.drawImage(img, 0, 0);
                canvas.toBlob((blob) => {
                  if (!blob) return;
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const ts = new Date().toISOString().replace(/[:.]/g, '-');
                  a.download = `cash-flow-${ts}.png`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                });
              };
              img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
            } else if (value === 'csv') {
              // Download CSV with Year and all series columns
              try {
                // CSV-escape: wrap in quotes if value contains comma, quote, or non-ASCII
                const csvEscape = (val) => {
                  const s = String(val);
                  if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
                    return '"' + s.replace(/"/g, '""') + '"';
                  }
                  return s;
                };
                const rows = [];
                // Build header: Year + each series label with currency in header
                const headerCols = [t('result.cashFlow.year'), ...seriesWithLabels.map(s => `${s.label} (${currency})`)];
                rows.push(headerCols.map(csvEscape).join(','));
                // Build data rows - each row is a year (raw numbers, currency is in header)
                normalized.forEach((row, idx) => {
                  const yearLabel = `${t('result.cashFlow.year')} ${idx + 1}`;
                  const vals = seriesWithLabels.map(s => row[s.key] !== undefined ? row[s.key] : '');
                  rows.push([csvEscape(yearLabel), ...vals].join(','));
                });
                const csvContent = rows.join('\n');
                // Prepend UTF-8 BOM so Excel correctly renders Unicode chars (€, £, ¥, etc.)
                const BOM = '\uFEFF';
                const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                const ts = new Date().toISOString().replace(/[:.]/g, '-');
                a.download = `cash-flow-${ts}.csv`;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                // Silent fail
              }
            }
            // Reset dropdown
            e.target.selectedIndex = 0;
          }}
        >
          <option value="" disabled hidden>{t('common.download')}</option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
        </div>
      </div>
      <div ref={legendRef} style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', padding:'4px 0 0 0', margin:'0 auto 2px auto', width:'100%' }} />
    </div>
  );
};

export default CashFlowChart;
